import { CompanyInfo, Config } from '@websky/core/src/context';
import i18next from 'i18next';
import { getLocale } from '@websky/core/src/utils';
import { Card, CardType } from '@websky/core/src/PromoLoader/types';
import { Locale } from '@websky/core/src/types';
import { OauthServiceType } from '@websky/graphql';

const isProd = process.env.NODE_ENV === 'production';

export const promoLoaders: Card[] = [
	{
		type: CardType.City,
		city: '',
		title: '',
		text: '',
		image: 'https://cdn.websky.aero/content/frontend/images/6R/banners/loader_noBG.gif'
	}
];

const currentLocale = getLocale() as string;

const getLocaleLink = () => {
	if (currentLocale === Locale.Russian) {
		return 'https://aero.alrosa.ru';
	} else {
		return 'https://aero.alrosa.ru/en';
	}
};
const localeLink = getLocaleLink();
const loyaltyLocaleLink = `https://aero.alrosa.ru/${currentLocale}/account/#/account/loyalty`;

const companyInfo: CompanyInfo = {
	iataCode: '6R',
	subsidyFaresConditionURL: '',
	loyaltyProgramName: currentLocale === Locale.Russian ? 'Алмазная миля' : 'Diamond Mile Loyalty Program',
	loyaltyProgramRegistrationUrl: loyaltyLocaleLink,
	privacyPolicy: `${localeLink}/security/privacy-policy`,
	multiInsurance: true,
	specialDocumentForInsurance: false,
	facebook: '',
	instagram: '',
	vk: 'https://vk.com/aero.alrosa.ru',
	twitter: '',
	baggage: `${localeLink}/for-passenger/about-bag`,
	meal: `${localeLink}/onboard/catering`,
	seat: `${localeLink}/extra-services/seat-selection`,
	legal: `${localeLink}/for-passenger/air-transport-rules`,
	webskySearchOrder: `https://booking.aero.alrosa.ru/websky/#/search-order`,
	insurance: '',
	loyaltyProgramAccountUrl: loyaltyLocaleLink,
	exarePrivacyPolicyURL: `${localeLink}/security/privacy-policy`,
	exareRulesURL: `${localeLink}/for-passenger/changes`,
	baggageAnimalsURL: `${localeLink}/extra-services/pet-transport`,
	loyalty: {
		links: [
			{
				title: 'Status',
				url: `${localeLink}/diamond-mile/statuses-privileges`
			},
			{
				title: 'Earn miles',
				url: `${localeLink}/diamond-mile/get-miles`
			},
			{
				title: 'Use miles',
				url: `${localeLink}/diamond-mile/flight-for-miles`
			},
			{
				title: 'FAQ',
				url: `${localeLink}/diamond-mile/miles-feedback`
			}
		]
	}
};

export const i18n: i18next.Resource = {
	ru: {
		Consents: {
			agreement_rules: `Я подтверждаю, что ознакомлен(а) и соглашаюсь <a data-type="termsOfTariff">с условиями тарифа, </a><a href="${localeLink}/for-passenger/changes" target="_blank">с правилами изменения данных в авиабилете</a>{{insurancesRules}}{{additionalServiceRules}}`,
			agreement_insurancesRules: `, <a href="http://www.alfastrah.ru/docs/rules_SP_230617.pdf" target="_blank">правилами страхования</a>, а также условиями {{insurancesProductsLinks}}`,
			agreement_gdsRules: `, оплатой выбранных дополнительных услуг.`
		},
		Checkout: {
			Agreement: `Я подтверждаю, что ознакомлен(а) и соглашаюсь с <a data-type="termsOfTariff">условиями тарифа</a>, <a href="${companyInfo.legal}" target="_blank">общими правилами перевозок</a>, с Правилами воздушных перевозок пассажиров, <a href="${companyInfo.baggage}" target="_blank">багажа и грузов</a> АО «Авиакомпания АЛРОСА» и правилами приобретения дополнительных услуг. Я разрешаю обработку и хранение <a href="${companyInfo.privacyPolicy}" target="_blank">персональных данных</a>.`,
			'You will not be able to change passenger details after purchasing a ticket.': `За изменение данных в авиабилете после бронирования может взыматься <a href='${localeLink}/for-passenger/changes' target='_blank'>дополнительная плата</a>.`
		},
		Title: {
			companyName: 'Авиакомпания АЛРОСА'
		},
		BaggagePage: {
			'Transportation of impressions no limitations': 'Какие сумки вы берете с собой в путешествие?',
			Baggage: 'Какие сумки вы берете с собой в путешествие?'
		},
		PromoCode: {
			Title: 'У вас есть промокод?'
		},
		AdditionalBaggage: {
			'Buy now and save 20-30%': 'Сэкономьте сейчас до 25%'
		},
		Loyalty: {
			'Log in to LightJet Club': 'Вход в личный кабинет Алмазная миля!'
		},
		InsurancePage: {
			'I do not want to insure my life or luggage and assume all risks': 'Беру все риски на себя'
		},
		SearchForm: {
			subsidiesLinkLabel: 'Субсидии'
		}
	},
	en: {
		Consents: {
			agreement_rules: `I confirm that I have read and agree to the <a data-type="termsOfTariff">fare terms, </a><a href="${localeLink}/for-passenger/changes" target="_blank">rules for changing data in the air ticket</a>{{insurancesRules}}{{agreement_gdsRules}}`,
			agreement_gdsRules: `, payment for the selected additional services.`
		},
		Checkout: {
			Agreement: `I confirm that I have read and agree to the <a data-type="termsOfTariff">fare conditions</a>, <a href="${companyInfo.legal}" target="_blank">general rules of carriage</a>, the Rules for the air carriage of passengers, <a href="${companyInfo.baggage}" target="_blank">baggage and cargo</a> of JSC ALROSA Airlines and the rules for purchasing additional services. I authorize the processing and storage of <a href="${companyInfo.privacyPolicy}" target="_blank">personal data</a>.`,
			'You will not be able to change passenger details after purchasing a ticket.': `Changes to flight ticket details after booking may be subject to an <a href='${localeLink}/for-passenger/changes' target='_blank'>additional fee</a>.`
		},
		Title: {
			companyName: 'ALROSA Airlines'
		},
		BaggagePage: {
			'Transportation of impressions no limitations': 'What bags are you taking on travel?',
			Baggage: 'What bags are you taking on travel?'
		},
		PromoCode: {
			Title: 'Do you have a promo code?'
		},
		AdditionalBaggege: {
			'Buy now and save 20-30%': 'Buy now and save up to 25%'
		},
		Loyalty: {
			'Log in to LightJet Club': 'Login to your personal account Diamond Mile!'
		},
		InsurancePage: {
			'I do not want to insure my life or luggage and assume all risks': 'I assume all risks'
		},
		SearchForm: {
			subsidiesLinkLabel: 'Subsidies'
		}
	}
};

export const config: Config = {
	SearchForm: {
		additionalLinks: [
			{
				title: 'subsidiesLinkLabel',
				url: `${getLocaleLink()}/for-passenger/granted-transport/`
			}
		],
		payByMiles: true,
		showFfpBonusInfoInFares: true,
		offerHotels: true,
		offerHotelsSupplier: 'OSTROVOK',
		offerHotelsLink: 'https://hotels.aero.alrosa.ru/hotels/',
		offerHotelsMainLink: 'https://hotels.alrosa.aero',
		showPricesInDatepicker: true,
		maxComplexRouteSegments: 4,
		enableComplexRoute: true,
		locationGroups: {
			showAdditionalGroups: false
		},
		separatedDatepicker: true
	},
	Engine: {
		promoLoaders: promoLoaders,
		checkFfpStatus: true
	},
	global: {
		showTaxesInfo: true,
		dynamicBaggagePricing: true,
		yandexMetrikaId: isProd ? '25213985' : undefined,
		companyInfo,
		i18n,
		allowPromoCodes: true,
		socialAuthMethods: [{ service: OauthServiceType.Vkontakte, isAvailable: false }],
		currentUserInGlobalScope: true,
		seatMap: {
			splitByCabin: true
		},
		splitCartFlightPassengers: true,
		showZeroCharges: true,
		additionalResources: {
			extraSvgs: ['https://cdn.websky.aero/content/6R/images/icons.svg']
		}
	},
	FlightStatus: {
		playMarketURL: '',
		appStoreURL: ''
	}
};
